/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable */
// hooks/travelRequest/useTravelRequest.js
import { useCallback, useMemo } from 'react';
import { useTravelRequestContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from '../apiEndpoints';
import Swal from 'sweetalert2';
import { TravelRequestSchema } from './validateTravelRequest';
import { useNavigate } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';

export const useTravelRequest = () => {
    const [state, dispatch] = useTravelRequestContext();
    const navigate = useNavigate();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const showErrorMessage = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const validateRequest = useCallback(async (requestData) => {
        try {
            await TravelRequestSchema.validate(requestData, { abortEarly: false });
            return { isValid: true, errors: {} };
        } catch (validationError) {
            const errors = validationError.inner.reduce((acc, error) => {
                acc[error.path] = error.message;
                return acc;
            }, {});
            throw new APIError('Validation failed', 422, errors);
        }
    }, []);

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) => {
                    if (key === 'dateRange') {
                        return [
                            createSearchCriteria({
                                key: 'startDate',
                                value: value.start,
                                operation: 'GREATER_THAN_EQUAL'
                            }),
                            createSearchCriteria({
                                key: 'endDate',
                                value: value.end,
                                operation: 'LESS_THAN_EQUAL'
                            })
                        ];
                    }
                    return createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    });
                })
                .flat();

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.TRAVEL_REQUESTS, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_REQUESTS, payload: data });
            return data;
        } catch (error) {
            showErrorMessage(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, showErrorMessage, dispatch, state]);

    const searchRequests = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            try {
                setLoading(true);

                const searchCriteria = Object.entries(searchParams).map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value: value.value,
                        operation: value.operation || 'EQUAL',
                        orPredicate: value.orPredicate || false
                    })
                );

                const pageable = createPageable({
                    page: pageParams.page || 0,
                    size: pageParams.size || 10,
                    sort: pageParams.sort || []
                });

                const data = await apiCaller.search(API_ENDPOINTS.TRAVEL_REQUESTS, searchCriteria, pageable);
                dispatch({ type: ACTIONS.SET_REQUESTS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const createRequest = useCallback(
        async (requestData) => {
            try {

                setLoading(true);
                const data = await apiCaller.postFormDatai(API_ENDPOINTS.TRAVEL_REQUESTS, requestData);

                const myRequest = data.find((request) => request.requestUser?.id === request.user?.id);

                if (myRequest) {
                    dispatch({ type: ACTIONS.ADD_REQUEST, payload: myRequest });
                }

                showSuccessMessage('Travel requests created successfully');
                navigate('/dashboard/my-trips');
                return data;
            } catch (error) {

                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch, navigate]
    );

    const updateRequest = useCallback(
        async (id, requestData) => {
            try {
                setLoading(true);
                await validateRequest(requestData);

                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${id}`, requestData);
                dispatch({ type: ACTIONS.UPDATE_REQUEST, payload: { id, data } });

                showSuccessMessage('Travel request updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch, validateRequest]
    );

    const deleteRequest = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_REQUEST, payload: id });

                showSuccessMessage('Travel request deleted successfully');
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const getRequestById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_REQUEST, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const addComment = useCallback(
        async (requestId, comment) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/comments`, comment);
                dispatch({
                    type: ACTIONS.ADD_REQUEST_COMMENT,
                    payload: data
                });
                showSuccessMessage('Comment added successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateRequestStatus = useCallback(
        async (requestId, status) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/statuses`, { status });
                dispatch({
                    type: ACTIONS.SET_REQUEST_STATUS,
                    payload: { id: requestId, status }
                });
                showSuccessMessage(`Request status updated to ${status}`);
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateApprover = useCallback(
        async (requestId, approverData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(
                    `${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/approvers/${approverData.approverId}`,
                    approverData
                );
                dispatch({
                    type: ACTIONS.UPDATE_REQUEST_APPROVER,
                    payload: data
                });
                showSuccessMessage('Approver updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateAttachments = useCallback(
        async (requestId, files) => {
            try {
                setLoading(true);
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));

                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/attachments`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                dispatch({
                    type: ACTIONS.UPDATE_ATTACHMENTS,
                    payload: data
                });
                showSuccessMessage('Attachments updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const searchByUserId = useCallback(
        async (userId, pageParams = {}) => {
            try {
                setLoading(true);

                const searchCriteria = [
                    createSearchCriteria({
                        key: 'user.id',
                        value: userId,
                        operation: 'EQUAL',
                        orPredicate: false
                    })
                ];

                const pageable = createPageable({
                    page: pageParams.page || 0,
                    size: pageParams.size || 100
                });

                const data = await apiCaller.search(API_ENDPOINTS.TRAVEL_REQUESTS, searchCriteria, pageable);
                dispatch({ type: ACTIONS.SET_REQUESTS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getPendingRequests = useCallback(
        async (currentApprovalStage, status) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();

                if (currentApprovalStage) {
                    queryParams.append('currentApprovalStage', currentApprovalStage);
                }
                if (status) {
                    queryParams.append('status', status);
                }

                const endpoint = `${API_ENDPOINTS.TRAVEL_REQUESTS}/approvals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

                const data = await apiCaller.get(endpoint);
                dispatch({ type: ACTIONS.SET_PENDING_REQUESTS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const bulkUpdateRequests = useCallback(
        async (requests) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/approvals`, requests);

                dispatch({
                    type: ACTIONS.BULK_UPDATE_REQUESTS,
                    payload: {
                        travelRequestIds: requests.travelRequestIds,
                        status: requests.status
                    }
                });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [dispatch, setLoading, showSuccessMessage, showErrorMessage]
    );

    const statusNotifications = useCallback(
        async (requestId) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/statuses`);
                dispatch({ type: ACTIONS.SET_STATUSES, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getLineActivities = useCallback(
        async (requestId) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_LINE_ACTIVITIES}/${requestId}`);
                dispatch({ type: ACTIONS.SET_LINE_ACTIVITIES, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const acquitTravel = useCallback(
        async (requestId, { acquittal, files }) => {
            try {
                setLoading(true);
                // await validateRequest(acquittalData);

                const formData = new FormData();

                // Add the acquittal data as a JSON blob
                formData.append(
                    'acquittal',
                    new Blob([JSON.stringify(acquittal)], {
                        type: 'application/json'
                    })
                );

                // Add all files
                if (files && files.length > 0) {
                    files.forEach((file) => {
                        formData.append('files', file);
                    });
                }

                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/acquit`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                dispatch({ type: ACTIONS.ACQUIT_TRAVEL_REQUEST, payload: { id: requestId, data } });
                secureLocalStorage.removeItem('perdiemReceipt');
                secureLocalStorage.removeItem('expenseData');
                showSuccessMessage('Travel request acquitted successfully');
                navigate(`/dashboard/my-trips`);
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const completeTrip = useCallback(
        async (requestId) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/complete`);
                
                dispatch({
                    type: ACTIONS.SET_REQUEST_STATUS,
                    payload: { id: requestId, status: 'COMPLETED' }
                });
                
                showSuccessMessage('Trip completed successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const selfDeclineTrip = useCallback(
        async (requestId, comments) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/self-decline`, {
                    travelRequestId: requestId,
                    comments
                });
                
                dispatch({
                    type: ACTIONS.SET_REQUEST_STATUS,
                    payload: { id: requestId, status: 'DECLINED' }
                });
                
                showSuccessMessage('Trip declined successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const getAcquittals = useCallback(
        async ({
            type = 'SELF',
            preparedByEmail,
            userEmail,
            status,
            branchCode,
            departmentCode,
            travelRequestId,
            acquittalId,
            createdAtFrom,
            createdAtTo,
            page = 0,
            size = 20
        } = {}) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();

                // Add non-null parameters to query string
                if (type) queryParams.append('type', type);
                if (preparedByEmail) queryParams.append('preparedByEmail', preparedByEmail);
                if (userEmail) queryParams.append('userEmail', userEmail);
                if (status) queryParams.append('status', status);
                if (branchCode) queryParams.append('branchCode', branchCode);
                if (departmentCode) queryParams.append('departmentCode', departmentCode);
                if (travelRequestId) queryParams.append('travelRequestId', travelRequestId.toString());
                if (acquittalId) queryParams.append('acquittalId', acquittalId.toString());
                if (createdAtFrom) queryParams.append('createdAtFrom', createdAtFrom);
                if (createdAtTo) queryParams.append('createdAtTo', createdAtTo);
                if (page !== undefined) queryParams.append('page', page.toString());
                if (size !== undefined) queryParams.append('size', size.toString());

                const data = await apiCaller.get(`${API_ENDPOINTS.ACQUITTALS}?${queryParams.toString()}`);
                dispatch({ type: ACTIONS.SET_ACQUITTALS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getTravelRequestsByType = useCallback(
        async ({
            type = 'ALL',
            search,
            userEmail,
            branchCode,
            departmentCode,
            travelRequestId,
            status,
            latestAcquittalId,
            createdAtFrom,
            createdAtTo,
            page = 0,
            size = 20,
            sort
        } = {}) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();

                // Add non-null parameters to query string
                if (type) queryParams.append('type', type);
                if (search) queryParams.append('search', search);
                if (userEmail) queryParams.append('userEmail', userEmail);
                if (branchCode) queryParams.append('branchCode', branchCode);
                if (departmentCode) queryParams.append('departmentCode', departmentCode);
                if (travelRequestId) queryParams.append('travelRequestId', travelRequestId.toString());
                if (status) queryParams.append('status', status);
                if (latestAcquittalId) queryParams.append('latestAcquittalId', latestAcquittalId.toString());
                if (createdAtFrom) queryParams.append('createdAtFrom', createdAtFrom);
                if (createdAtTo) queryParams.append('createdAtTo', createdAtTo);
                if (page !== undefined) queryParams.append('page', page.toString());
                if (size !== undefined) queryParams.append('size', size.toString());
                if (sort) queryParams.append('sort', sort);

                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}?${queryParams.toString()}`);
                dispatch({ type: ACTIONS.SET_REQUESTS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getTravelMatrix = useCallback(
        async (search = 'all') => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                queryParams.append('search', search);

                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}/travel-matrix?${queryParams.toString()}`);
                dispatch({ type: ACTIONS.SET_TRAVEL_MATRIX, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getTravelRequestDocuments = useCallback(
        async (requestId, pageParams = { page: 0, size: 10 }) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                queryParams.append('page', pageParams.page.toString());
                queryParams.append('size', pageParams.size.toString());

                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/documents?${queryParams.toString()}`);
                dispatch({ type: ACTIONS.SET_DOCUMENTS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const nudgePendingApprover = useCallback(
        async (requestId) => {
            try {
                return await apiCaller.post(`${API_ENDPOINTS.TRAVEL_REQUESTS}/${requestId}/nudge`);
            } catch (error) {
                showErrorMessage(error);
                throw error;
            }
        },
        [showErrorMessage]
    );

    return useMemo(
        () => ({
            // State
            requests: state.requests,
            selectedRequest: state.selectedRequest,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            pendingRequests: state.pendingRequests,
            lineActivities: state.lineActivities,
            statuses: state.statuses,
            travelMatrix: state.travelMatrix,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchRequests,
            searchRequests,
            searchByUserId,
            getPendingRequests,
            getLineActivities,
            statusNotifications,
            getTravelRequestsByType,
            getAcquittals,
            getTravelMatrix,
            getTravelRequestDocuments,
            nudgePendingApprover,
            // CRUD Operations
            createRequest,
            updateRequest,
            deleteRequest,
            getRequestById,
            bulkUpdateRequests,
            acquitTravel,

            // Additional Operations
            addComment,
            updateRequestStatus,
            updateApprover,
            updateAttachments,
            clearError,
            completeTrip,
            selfDeclineTrip,

            // Utilities
            validateRequest
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchRequests,
            searchRequests,
            createRequest,
            updateRequest,
            deleteRequest,
            getRequestById,
            addComment,
            updateRequestStatus,
            updateApprover,
            updateAttachments,
            clearError,
            validateRequest,
            searchByUserId,
            bulkUpdateRequests,
            getPendingRequests,
            getLineActivities,
            statusNotifications,
            acquitTravel,
            completeTrip,
            selfDeclineTrip,
            getTravelRequestsByType,
            getAcquittals,
            nudgePendingApprover
        ]
    );
};

export default useTravelRequest;
